/**
 * Iter-157A Phase 5 — Restore preview drawer.
 *
 * Lives on the right of the Attic page (or beneath, on mobile) and
 * answers the producer's three questions before they commit:
 *
 *   1. What will change?  — per-collection diff samples + counts,
 *                            calmly phrased ("EP1 sc12 — INT. CHAPEL"
 *                            not "scene_id: 4d7…").
 *   2. Is it safe?        — locked safety lines from the backend
 *                            preview payload + warnings.
 *   3. How do I confirm?  — type-to-confirm gate on the project name,
 *                            then the Restore button activates.
 *
 * For archived projects, the Restore button is replaced by a calm
 * "Restore to a copy" CTA (the in-place restore endpoint is refused
 * by the backend, and "Restore to a copy" is the safer, more honest
 * action anyway).
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, AlertCircle, Copy, X, Pin, PinOff, Download } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import AtticCloneDialog from "./AtticCloneDialog";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";


function fmtTime(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch (_) { return iso; }
}


// Renders identity-only sample rows produced by the backend preview.
// Keep this dumb on purpose — the backend already shaped each row for
// display. We just pick the right human caption.
function SampleLine({ coll, row }) {
    if (coll === "scenes") {
        const ep = row.episode ? `EP${row.episode} ` : "";
        return (
            <span>
                {ep}sc{row.scene_number || "—"}
                {row.heading ? ` — ${row.heading}` : ""}
            </span>
        );
    }
    if (coll === "characters") {
        return (
            <span>
                {row.name || "Untitled character"}
                {row.team ? <span className="text-neutral-500"> · {row.team}</span> : null}
            </span>
        );
    }
    if (coll === "scene_characters") {
        return (
            <span>
                {row.character_name || "—"}
                <span className="text-neutral-500">
                    {" in sc"}{row.scene_number || "—"}
                </span>
            </span>
        );
    }
    if (coll === "state_entries") {
        return (
            <span>
                {row.character_name || "—"}
                <span className="text-neutral-500">
                    {" · sc"}{row.scene_number || "—"}
                    {row.look ? ` · ${row.look}` : ""}
                </span>
            </span>
        );
    }
    if (coll === "supervisor_notes") {
        return (
            <span>
                <span className="text-neutral-500">sc{row.scene_number || "—"} · </span>
                {row.text_snippet || "(no text)"}
            </span>
        );
    }
    if (coll === "change_documents") {
        return (
            <span>
                {row.label || row.filename || row.link_url || "Document"}
                {row.kind ? <span className="text-neutral-500"> · {row.kind}</span> : null}
            </span>
        );
    }
    return <span>{row.name || row.id}</span>;
}


function DiffSamples({ coll, samples }) {
    const added = samples?.added || [];
    const removed = samples?.removed || [];
    const changed = samples?.changed || [];
    if (!added.length && !removed.length && !changed.length) return null;
    return (
        <div className="mt-1.5 space-y-1 text-xs text-neutral-400">
            {added.slice(0, 3).map((row, i) => (
                <div key={`a-${i}`} className="flex gap-2">
                    <span className="text-emerald-400/70 select-none">+</span>
                    <SampleLine coll={coll} row={row} />
                </div>
            ))}
            {removed.slice(0, 3).map((row, i) => (
                <div key={`r-${i}`} className="flex gap-2">
                    <span className="text-rose-400/70 select-none">−</span>
                    <SampleLine coll={coll} row={row} />
                </div>
            ))}
            {changed.slice(0, 2).map((pair, i) => (
                <div key={`c-${i}`} className="flex gap-2">
                    <span className="text-sky-400/70 select-none">~</span>
                    <div className="space-y-0.5 min-w-0 flex-1">
                        <SampleLine coll={coll} row={pair.before || {}} />
                        <div className="text-neutral-500 text-[11px] pl-2">
                            ↳ <SampleLine coll={coll} row={pair.after || {}} />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}


// Collections rendered in the diff section, in the order producers
// scan them. Skip rows whose counts are all zero so the diff stays
// calm on no-op restores.
const DIFF_COLLS = [
    ["scenes", "Scenes"],
    ["characters", "Characters"],
    ["scene_characters", "Scene cast"],
    ["state_entries", "Continuity entries"],
    ["supervisor_notes", "Supervisor notes"],
    ["change_documents", "Documents & links"],
    ["schedules", "Schedules"],
    ["flags", "Flags"],
];


export default function AtticPreviewDrawer({
    project,
    rp,
    onClose,
    onAfterRestore,
    onTogglePin,
}) {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [typed, setTyped] = useState("");
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [showClone, setShowClone] = useState(false);
    const navigate = useNavigate();
    const apiBase = useApiBase();
    const routeBase = useRouteBase();
    // Iter-158.13.2.20e.14 — Route 2 clone-to-new-project endpoint is
    // now live at `POST /api/beta/projects/{pid}/restore-points/{rp_id}/clone-to-new-project`
    // (Option B: recovered state becomes a genuinely NEW Production).
    // The `.20e.13` `isBetaScope` guard that hid the "Restore to a
    // copy" CTAs on beta has been removed; both mounts now render
    // the full active/archived CTA pair.

    useEffect(() => {
        let cancelled = false;
        if (!rp) {
            setPreview(null);
            return undefined;
        }
        setLoading(true);
        setTyped("");
        setReason("");
        api.post(`${apiBase}/${project.id}/restore-points/${rp.id}/preview`)
            .then((res) => {
                if (!cancelled) setPreview(res.data);
            })
            .catch((err) => {
                if (!cancelled) {
                    toast.error(formatApiError(err, "Could not load preview."));
                    setPreview(null);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [project?.id, rp?.id, apiBase]);

    const projectName = (project?.name || "").trim();
    const nameMatches = typed.trim() === projectName && projectName.length > 0;
    const restoreBlocked = preview?.restore_blocked === true;

    const nonZeroDiffEntries = useMemo(() => {
        if (!preview?.diff) return [];
        return DIFF_COLLS.filter(([k]) => {
            const d = preview.diff[k];
            if (!d) return false;
            return (d.added_count || 0) + (d.removed_count || 0) + (d.changed_count || 0) > 0;
        });
    }, [preview]);

    const handleRestore = async () => {
        if (submitting || !nameMatches || restoreBlocked) return;
        setSubmitting(true);
        try {
            const res = await api.post(
                `${apiBase}/${project.id}/restore-points/${rp.id}/restore`,
                { confirm_name: typed.trim(), reason: reason.trim() || null },
            );
            onAfterRestore?.(res.data);
        } catch (err) {
            toast.error(formatApiError(err, "Restore failed. Your project is unchanged."));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDownload = () => {
        if (!rp) return;
        const url = `${api.defaults.baseURL}/projects/${project.id}/restore-points/${rp.id}/download`;
        const token = localStorage.getItem("onsetflow_token");
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob())
            .then((blob) => {
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = `restore-point-${rp.id}.json`;
                a.click();
            })
            .catch(() => toast.error("Download failed."));
    };

    if (!rp) return null;

    return (
        <aside
            data-testid="attic-preview-drawer"
            className="rounded-xl border border-white/10 bg-card p-5 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto"
        >
            <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                        Preview restore
                    </div>
                    <h2
                        className="text-base font-semibold text-neutral-100 mt-0.5 leading-snug break-words"
                        data-testid="attic-preview-label"
                    >
                        {rp.label || "Restore point"}
                    </h2>
                    <div className="text-[11px] text-neutral-500 mt-1">
                        {fmtTime(rp.created_at)}
                        {rp.created_by_name ? ` · ${rp.created_by_name}` : ""}
                    </div>
                </div>
                <button
                    type="button"
                    data-testid="attic-preview-close"
                    onClick={onClose}
                    className="p-1.5 rounded-md text-neutral-500 hover:text-neutral-200 hover:bg-white/5"
                    aria-label="Close preview"
                >
                    <X size={16} />
                </button>
            </div>

            {/* Pin / download row — low-prominence utility actions. */}
            <div className="flex flex-wrap gap-1.5 mb-4">
                <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    data-testid="attic-preview-toggle-pin"
                    onClick={() => onTogglePin?.(rp)}
                    disabled={project.archived}
                    title={project.archived ? "Archived projects are frozen" : undefined}
                >
                    {rp.pinned ? (
                        <span className="inline-flex items-center gap-1.5">
                            <PinOff size={12} /> Unpin
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1.5">
                            <Pin size={12} /> Pin
                        </span>
                    )}
                </Button>
                <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    data-testid="attic-preview-download"
                    onClick={handleDownload}
                >
                    <span className="inline-flex items-center gap-1.5">
                        <Download size={12} /> Download
                    </span>
                </Button>
            </div>

            {/* What will change — calm per-collection summary. */}
            {loading ? (
                <div className="flex items-center gap-2 text-sm text-neutral-400 py-6">
                    <Loader2 size={14} className="animate-spin" />
                    Reading the snapshot…
                </div>
            ) : preview ? (
                <>
                    <section className="mb-4">
                        <h3 className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">
                            What will change
                        </h3>
                        {nonZeroDiffEntries.length === 0 ? (
                            <p
                                className="text-sm text-neutral-400"
                                data-testid="attic-preview-noop"
                            >
                                Nothing — the project state matches this restore
                                point exactly. Restoring is safe and will produce
                                a fresh safety snapshot.
                            </p>
                        ) : (
                            <div className="space-y-3" data-testid="attic-preview-diff">
                                {nonZeroDiffEntries.map(([key, label]) => {
                                    const d = preview.diff[key];
                                    const samples = preview.samples?.[key] || {};
                                    return (
                                        <div key={key} data-testid={`attic-diff-${key}`}>
                                            <div className="flex items-center justify-between gap-2 text-xs">
                                                <span className="text-neutral-200">{label}</span>
                                                <span className="text-neutral-500 font-mono text-[11px]">
                                                    {d.added_count ? `+${d.added_count} ` : ""}
                                                    {d.removed_count ? `-${d.removed_count} ` : ""}
                                                    {d.changed_count ? `~${d.changed_count}` : ""}
                                                </span>
                                            </div>
                                            <DiffSamples coll={key} samples={samples} />
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </section>

                    {/* Calm warnings. */}
                    {(preview.warnings || []).length > 0 ? (
                        <section className="mb-4" data-testid="attic-preview-warnings">
                            <h3 className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">
                                Worth knowing
                            </h3>
                            <ul className="space-y-1.5">
                                {preview.warnings.map((w, i) => (
                                    <li
                                        key={i}
                                        className="flex gap-2 text-xs text-amber-100/90 leading-relaxed"
                                    >
                                        <AlertCircle
                                            size={12}
                                            className="mt-0.5 shrink-0 text-amber-300/80"
                                        />
                                        <span>{w}</span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    ) : null}

                    {/* Safety lines — always present, locked copy. */}
                    <section className="mb-5" data-testid="attic-preview-safety">
                        <div className="rounded-md border border-emerald-500/35 bg-emerald-500/[0.08] px-3 py-2.5">
                            <div className="flex gap-2 items-start">
                                <ShieldCheck
                                    size={13}
                                    className="text-emerald-600 dark:text-emerald-300/80 mt-0.5 shrink-0"
                                />
                                <ul className="text-[12px] text-emerald-800 dark:text-emerald-100/90 leading-relaxed space-y-0.5">
                                    {(preview.safety || []).map((line, i) => (
                                        <li key={i}>{line}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </section>

                    {/* CTA panel — restore (active) OR clone (archived). */}
                    {restoreBlocked ? (
                        <div data-testid="attic-preview-archived-cta">
                            <p className="text-xs text-neutral-400 leading-relaxed mb-3">
                                {preview.restore_blocked_reason ||
                                    "This project is archived. Use \u201cRestore to a copy\u201d to spawn a new project from this point — the archive is never touched."}
                            </p>
                            <Button
                                type="button"
                                data-testid="attic-preview-clone-cta"
                                onClick={() => setShowClone(true)}
                                className="w-full font-semibold"
                            >
                                <span className="inline-flex items-center gap-2">
                                    <Copy size={14} /> Restore to a copy
                                </span>
                            </Button>
                        </div>
                    ) : (
                        <div>
                            <label
                                htmlFor="attic-preview-typed"
                                className="block text-[11px] uppercase tracking-[0.22em] text-neutral-500 mb-1.5"
                            >
                                Confirm by typing the project name
                            </label>
                            <Input
                                id="attic-preview-typed"
                                data-testid="attic-preview-confirm-input"
                                value={typed}
                                onChange={(e) => setTyped(e.target.value)}
                                placeholder={projectName}
                                autoComplete="off"
                                disabled={submitting}
                            />
                            <Textarea
                                data-testid="attic-preview-reason-input"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Optional reason for the audit log"
                                rows={2}
                                maxLength={400}
                                className="mt-2 resize-none text-sm"
                                disabled={submitting}
                            />
                            <div className="mt-3 flex flex-col gap-2">
                                {/* Iter-158.13.2.20f.1 (#10) — the
                                    normal safe recovery route is
                                    Restore to Copy (new Production).
                                    Restore in place remains available
                                    but as a secondary/advanced
                                    action; the confirmation-typing
                                    guard is preserved for the in-
                                    place path. */}
                                <Button
                                    type="button"
                                    data-testid="attic-preview-clone-primary"
                                    onClick={() => setShowClone(true)}
                                    className="w-full font-semibold"
                                >
                                    <span className="inline-flex items-center gap-2">
                                        <Copy size={14} /> Restore to a copy
                                    </span>
                                </Button>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    data-testid="attic-preview-restore-confirm"
                                    onClick={handleRestore}
                                    disabled={!nameMatches || submitting}
                                    className="w-full text-neutral-400"
                                >
                                    {submitting ? (
                                        <span className="inline-flex items-center gap-2">
                                            <Loader2 size={14} className="animate-spin" />
                                            Restoring in place…
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-2">
                                            Or restore in place (advanced)
                                        </span>
                                    )}
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <p className="text-sm text-neutral-500 py-6">
                    Preview unavailable.
                </p>
            )}

            {showClone ? (
                <AtticCloneDialog
                    open
                    project={project}
                    rp={rp}
                    onClose={() => setShowClone(false)}
                    onCloned={(result) => {
                        // Iter-157P.18.4 — navigate straight into the
                        // restored copy so the producer sees the work
                        // they just rescued, instead of being dropped
                        // back into the archive view.
                        const newId = result?.new_project_id;
                        if (newId) {
                            navigate(`${routeBase}/${newId}`);
                        }
                    }}
                />
            ) : null}
        </aside>
    );
}
